import type { NextPage } from 'next'
import { DefaultSeo } from 'next-seo'
import { IntercomProvider } from 'react-use-intercom'

import { ApolloProvider } from '@apollo/client'
import { config } from '@fortawesome/fontawesome-svg-core'
import { i18n } from '@lingui/core'
import { I18nProvider } from '@lingui/react'
import AuthorizationsContext from 'Root/App/Contexts/Authorizations'
import NoteContext from 'Root/App/Contexts/Note'
import RouterHistory from 'Root/Contexts/RouterHistory'
import TokenContext from 'Root/Contexts/Token'
import Middlewares from 'Root/Middlewares'
import CollectionContext from 'Root/Pages/Collection/Component/Context'
import PageLayout from 'Root/Pages/Layout'
import apolloClient from 'lib/apollo-client'
import { POLICY_LANG_CONFIG } from 'lib/constants'
import getQueryString from 'lib/get-query-string'
import { useLinguiInit } from 'lib/i18n'
import type { AppProps } from 'next/app'
import { useRouter } from 'next/router'
import Script from 'next/script'

import SEO from '../../next-seo.config'

import '@egjs/flicking-plugins/dist/arrow.css'
import '@egjs/flicking-plugins/dist/pagination.css'
import '@egjs/react-flicking/dist/flicking.css'
import '@fortawesome/fontawesome-svg-core/styles.css'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-sliding-pane/dist/react-sliding-pane.css'
import 'styles/shared.css'
import 'tailwindcss/tailwind.css'

config.autoAddCss = false

const getIubendaPolicyConfig = (locale) =>
  JSON.stringify({
    askConsentAtCookiePolicyUpdate: true,
    cookiePolicyInOtherWindow: true,
    enableFadp: true,
    enableLgpd: true,
    enableUspr: false,
    fadpApplies: true,
    floatingPreferencesButtonDisplay: false,
    perPurposeConsent: true,
    preferenceCookie: { expireAfter: 180 },
    siteId: 3714938,
    usprApplies: true,
    usPreferencesWidgetDisplay: 'inline-center',
    whitelabel: false,
    cookiePolicyId: POLICY_LANG_CONFIG[locale],
    lang: locale,
    banner: {
      acceptButtonColor: '#9489FA',
      acceptButtonDisplay: true,
      backgroundColor: '#111827',
      backgroundOverlay: true,
      closeButtonDisplay: false,
      continueWithoutAcceptingButtonColor: '#1F2937',
      continueWithoutAcceptingButtonDisplay: false,
      customizeButtonColor: '#1F2937',
      customizeButtonDisplay: true,
      explicitWithdrawal: true,
      listPurposes: true,
      logo: null,
      ownerName: 'https:taylora.com',
      position: 'bottom',
      rejectButtonColor: '#1F2937',
      rejectButtonDisplay: true,
      showTitle: false,
      showTotalNumberOfProviders: true,
      zIndex: 2147483004,
    },
  })

const App: NextPage<AppProps> = ({ Component, pageProps }: AppProps) => {
  const { query } = useRouter()
  useLinguiInit(pageProps.translation)

  return (
    <TokenContext>
      <ApolloProvider client={apolloClient}>
        <RouterHistory>
          <CollectionContext>
            <I18nProvider i18n={i18n}>
              <AuthorizationsContext>
                <NoteContext>
                  <Middlewares>
                    <DefaultSeo {...SEO} />
                    <IntercomProvider
                      appId={process.env.NEXT_PUBLIC_INTERCOM_APP_ID}
                      autoBoot
                    >
                      <PageLayout>
                        <Component {...pageProps} />
                      </PageLayout>
                    </IntercomProvider>

                    <Script
                      src={`https://www.googletagmanager.com/gtag/js?id=${process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_4}`}
                    />

                    <Script id="iubenda-config">
                      {`var _iub = _iub || [];
                        _iub.csConfiguration = ${getIubendaPolicyConfig(
                          getQueryString(query.locale)
                        )};`}
                    </Script>

                    <Script
                      id="iubenda-blocking"
                      src="https://cs.iubenda.com/autoblocking/3714938.js"
                    />
                    <Script
                      id="iubenda-stub"
                      src="//cdn.iubenda.com/cs/gpp/stub.js"
                    />

                    <Script
                      id="iubenda-cs"
                      src="//cdn.iubenda.com/cs/iubenda_cs.js"
                    />
                  </Middlewares>
                </NoteContext>
              </AuthorizationsContext>
            </I18nProvider>
          </CollectionContext>
        </RouterHistory>
      </ApolloProvider>
    </TokenContext>
  )
}

export default App
